import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { useSelector } from 'react-redux'
// components
import CircularProgress from '@mui/material/CircularProgress'
import { InputLabel } from 'components/FormElements'
// graphql
import { GET_PARENT_FORM_PROPERTIES_QUERY } from 'graphql/risk/queries'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// types
import { IField, IParentFormPropertiesItem, IRisk } from 'types/graphql'
// icons
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
// views
import { Wrapper, RowsContainer, ResultRow, RecordLink, ParentRecordLink, Postfix } from './views'
// utils
import { formatResolvedValue } from './utils'
import { getFieldsInitialValues } from 'utils/form'

interface Props {
  field: IField
}

const RecordItem = ({ record, fieldValue }: { record: IRisk; fieldValue?: string }) => (
  <span>
    <RecordLink href={`/forms/edit/${record.id}/template/${record.riskTemplate.id}`} target={'_blank'}>
      {record.riskTemplate?.name}
    </RecordLink>
    {record.customerUniqueIdPostfix && <Postfix> [{record.customerUniqueIdPostfix}]</Postfix>}
    {fieldValue && <span> "{fieldValue}"</span>}
  </span>
)

const ParentRiskItem = ({
  parentRisk,
  fieldValue,
  onClick
}: {
  parentRisk: IRisk
  fieldValue?: string
  onClick: () => void
}) => (
  <span>
    <ParentRecordLink onClick={onClick}>{parentRisk.riskTemplate?.name}</ParentRecordLink>
    {parentRisk.customerUniqueIdPostfix && <Postfix> [{parentRisk.customerUniqueIdPostfix}]</Postfix>}
    {fieldValue && <span> "{fieldValue}"</span>}
  </span>
)

const MainRiskItem = ({
  onClick,
  mainRisk,
  fieldValue,
}: {
  onClick: () => void
  mainRisk: IRisk
  fieldValue?: string
}) => (
  <span>
    <ParentRecordLink onClick={onClick}>{mainRisk.riskTemplate?.name}</ParentRecordLink>
    {mainRisk.customerUniqueIdPostfix && <Postfix> [{mainRisk.customerUniqueIdPostfix}]</Postfix>}
    {fieldValue && <span> "{fieldValue}"</span>}
  </span>
)

const RecordLinks: React.FC<Props> = ({ field }) => {
  const { client } = useApolloClient()

  const { risk } = React.useContext(RiskAPIContext)
  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const riskTemplates = useSelector(riskTemplatesSelector)

  const fieldMappings = (field.settings as any)?.fieldMappings ?? []

  const { data, loading } = useQuery<any>(GET_PARENT_FORM_PROPERTIES_QUERY, {
    variables: { riskId: risk?.id, fieldMappings },
    skip: isRiskTemplate || !risk?.id
  })

  const [resolvedValues, setResolvedValues] = React.useState<Record<number, Record<string, any>>>({})

  const results: IParentFormPropertiesItem[] = data?.getParentFormProperties ?? []

  React.useEffect(() => {
    if (!results.length) {
      return
    }

    const resolve = async () => {
      const entries = await Promise.all(
        results.map(async (result, idx) => {
          const templateIds = result.fieldValues.map((fv) => fv.entityId).filter(Boolean)
          const fields = riskTemplates
            .filter((t) => templateIds.includes(t.id))
            .flatMap((t) => t.fields ?? [])
            .filter(Boolean) as IField[]

          const initialValues = await getFieldsInitialValues({
            client,
            fields,
            fieldValues: result.fieldValues
          })

          return [idx, initialValues] as const
        })
      )

      setResolvedValues(Object.fromEntries(entries))
    }

    resolve()
  }, [results])

  const hasContent = results.some((r) => r.ancestors.length > 0 || r.mainRisk)

  if (!loading && !isRiskTemplate && !hasContent) {
    return null
  }

  return (
    <Wrapper isRiskTemplate={isRiskTemplate} data-testid='risk-field-value' data-entity-id={field.id}>
      <InputLabel label={field.label} required={field.required} />
      {!isRiskTemplate && loading && <CircularProgress size={16} />}
      <RowsContainer>
        {results.map((result, idx) => {
          const records = [result.mainRisk, ...result.ancestors].filter(Boolean)

          // RecordLinks only ever maps one field per template (its own restriction, unlike Parent
          // Related Forms) — one value per record, not several.
          const getFieldValueFor = (record: IRisk): string | undefined => {
            const fieldValue = result.fieldValues?.find((item) => item.entityId === record.riskTemplate?.id)
            const resolvedValue = fieldValue?.fieldName ? resolvedValues[idx]?.[fieldValue.fieldName] : undefined

            return fieldValue ? formatResolvedValue(resolvedValue ?? fieldValue.fieldValue, fieldValue.fieldType) : undefined
          }

          const handleParentRiskClick = () =>
            window.open(`/forms/edit/${result.parentRisk!.id}/template/${result.parentRisk!.riskTemplate.id}`, '_blank')

          const handleMainRiskClick = () =>
            window.open(`/forms/edit/${result.mainRisk.id}/template/${result.mainRisk.riskTemplate.id}`, '_blank')

          return (
            <ResultRow
              key={idx}
              $even={idx % 2 === 0}
              data-testid='parent-related-form-row'
              data-entity-id={result.mainRisk?.id ?? idx}>
              {result.parentRisk && (
                <React.Fragment>
                  <ParentRiskItem
                    parentRisk={result.parentRisk}
                    fieldValue={getFieldValueFor(result.parentRisk)}
                    onClick={handleParentRiskClick}
                  />
                  <ArrowRightIcon />
                </React.Fragment>
              )}
              {records.map((record, recordIdx) => {
                const fieldValue = getFieldValueFor(record)

                return (
                  <React.Fragment key={record.id ?? record.riskTemplate?.id}>
                    {recordIdx > 0 && <ArrowRightIcon />}
                    {recordIdx === 0 && result.parentRisk
                      ? <MainRiskItem mainRisk={record} fieldValue={fieldValue} onClick={handleMainRiskClick} />
                      : <RecordItem record={record} fieldValue={fieldValue} />
                    }
                  </React.Fragment>
                )
              })}
            </ResultRow>
          )
        })}
      </RowsContainer>
    </Wrapper>
  )
}

export default RecordLinks
